import { useCallback, useEffect, useState } from "react";
import { heroBanners } from "@/mocks/heroBanners";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const total = heroBanners.length;

  const go = useCallback(
    (dir: number) => setIndex((i) => (i + dir + total) % total),
    [total],
  );

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % total), 6500);
    return () => clearInterval(timer);
  }, [total]);

  const active = heroBanners[index];

  return (
    <section id="top" className="relative w-full">
      <div className="relative h-[540px] md:h-[660px] lg:h-[720px] w-full overflow-hidden bg-foreground-950">
        {heroBanners.map((banner, i) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-[1100ms] ease-out ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={banner.image}
              alt={banner.title}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}

        <div className="absolute inset-0 hero-mask" />

        <div className="relative z-10 h-full w-full flex items-end">
          <div className="w-full px-4 md:px-8 pb-10 md:pb-16">
            <div className="flex items-end justify-between gap-8">
              <div key={active.id} className="max-w-2xl animate-fade-up">
                <span className="inline-flex items-center gap-2 font-label text-[11px] tracking-[0.3em] uppercase text-accent-400">
                  <span className="w-6 h-px bg-accent-400" />
                  Now Showing
                </span>
                <h1 className="mt-4 font-heading font-extrabold uppercase leading-[0.95] tracking-tight text-background-50 text-4xl md:text-6xl lg:text-7xl">
                  {active.title}
                </h1>
                <p className="mt-4 text-base md:text-lg text-background-200/90 max-w-xl">
                  {active.tagline}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <a
                    href="#library"
                    className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-xs tracking-[0.18em] uppercase px-6 py-3.5 whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    Watch Trailer
                  </a>
                  <a
                    href="#library"
                    className="inline-flex items-center gap-2 rounded-md border border-background-100/40 hover:border-accent-400 hover:text-accent-400 text-background-50 font-label text-xs tracking-[0.18em] uppercase px-6 py-3.5 whitespace-nowrap transition-colors cursor-pointer"
                  >
                    More Info
                  </a>
                  <span className="hidden sm:flex items-center gap-3 pl-2 text-xs text-background-200/70">
                    <span>{active.genre}</span>
                    <span className="w-1 h-1 rounded-full bg-background-200/50" />
                    <span>{active.year}</span>
                  </span>
                </div>
              </div>

              <div className="hidden lg:flex flex-col items-end gap-4 shrink-0">
                <span className="font-heading text-sm text-background-100/70 tabular-nums">
                  <span className="text-3xl text-background-50">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {" / "}
                  {String(total).padStart(2, "0")}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Previous slide"
                    onClick={() => go(-1)}
                    className="flex items-center justify-center w-11 h-11 rounded-full border border-background-100/35 text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>
                  </button>
                  <button
                    type="button"
                    aria-label="Next slide"
                    onClick={() => go(1)}
                    className="flex items-center justify-center w-11 h-11 rounded-full border border-background-100/35 text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-2">
              {heroBanners.map((banner, i) => (
                <button
                  key={banner.id}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1 rounded-full transition-all duration-500 cursor-pointer ${
                    i === index ? "w-10 bg-accent-400" : "w-4 bg-background-100/40 hover:bg-background-100/70"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="Previous slide"
          onClick={() => go(-1)}
          className="lg:hidden absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-foreground-950/40 text-background-50 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="Next slide"
          onClick={() => go(1)}
          className="lg:hidden absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-foreground-950/40 text-background-50 cursor-pointer"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}